import { useState, useRef, useCallback } from "react";

interface AppStoreApp {
  trackId: number;
  bundleId: string;
  appName: string;
  iconUrl?: string;
  description?: string;
  category: string;
  version?: string;
  developerName: string;
}

interface LookupResult {
  bundleId: string;
  app?: AppStoreApp;
  found: boolean;
  error?: string;
}

export const useAppStoreLookup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, LookupResult>>(new Map());
  const pendingRequestsRef = useRef<Map<string, Promise<LookupResult>>>(new Map());

  const lookupByBundleId = useCallback(async (bundleId: string): Promise<LookupResult> => {
    // Check cache first
    if (cacheRef.current.has(bundleId)) {
      return cacheRef.current.get(bundleId)!;
    }

    // Check if request is already pending
    if (pendingRequestsRef.current.has(bundleId)) {
      return pendingRequestsRef.current.get(bundleId)!;
    }

    // Create new request promise
    const requestPromise = (async (): Promise<LookupResult> => {
      try {
        const response = await fetch(
          `https://itunes.apple.com/lookup?bundleId=${encodeURIComponent(bundleId)}&country=US`
        );
        
        if (!response.ok) {
          throw new Error('Lookup failed');
        }
        
        const result = await response.json();
        
        let lookupResult: LookupResult;
        
        if (result.results && result.results.length > 0) {
          const item = result.results[0];
          const app: AppStoreApp = {
            trackId: item.trackId,
            bundleId: item.bundleId,
            appName: item.trackName,
            iconUrl: item.artworkUrl100 || item.artworkUrl60,
            description: item.description,
            category: item.primaryGenreName,
            version: item.version,
            developerName: item.artistName
          };
          
          lookupResult = {
            bundleId,
            app,
            found: true
          };
        } else {
          // Not found in iTunes - likely a system app
          lookupResult = {
            bundleId,
            found: false
          };
        }

        // Cache the result
        cacheRef.current.set(bundleId, lookupResult);
        return lookupResult;
      } catch (error) {
        console.error('iTunes lookup error:', error);
        const errorResult: LookupResult = {
          bundleId,
          found: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
        
        // Cache error results too to prevent retrying immediately
        cacheRef.current.set(bundleId, errorResult);
        return errorResult;
      } finally {
        // Remove from pending requests
        pendingRequestsRef.current.delete(bundleId);
      }
    })();

    // Store the pending request
    pendingRequestsRef.current.set(bundleId, requestPromise);
    
    return requestPromise;
  }, []);

  const lookupByTrackId = useCallback(async (trackId: string): Promise<LookupResult> => {
    const cacheKey = `id:${trackId}`;
    
    // Check cache first
    if (cacheRef.current.has(cacheKey)) {
      return cacheRef.current.get(cacheKey)!;
    }

    // Check if request is already pending
    if (pendingRequestsRef.current.has(cacheKey)) {
      return pendingRequestsRef.current.get(cacheKey)!;
    }

    // Create new request promise
    const requestPromise = (async (): Promise<LookupResult> => {
      try {
        const response = await fetch(
          `https://itunes.apple.com/lookup?id=${encodeURIComponent(trackId)}&country=US`
        );
        
        if (!response.ok) {
          throw new Error('Lookup by trackId failed');
        }
        
        const result = await response.json();
        
        let lookupResult: LookupResult;
        
        if (result.results && result.results.length > 0) {
          const item = result.results[0];
          const app: AppStoreApp = {
            trackId: item.trackId,
            bundleId: item.bundleId,
            appName: item.trackName,
            iconUrl: item.artworkUrl100 || item.artworkUrl60,
            description: item.description,
            category: item.primaryGenreName,
            version: item.version,
            developerName: item.artistName
          };
          
          lookupResult = {
            bundleId: item.bundleId,
            app,
            found: true
          };
        } else {
          lookupResult = {
            bundleId: '',
            found: false
          };
        }

        // Cache the result
        cacheRef.current.set(cacheKey, lookupResult);
        return lookupResult;
      } catch (error) {
        console.error('iTunes lookup by trackId error:', error);
        const errorResult: LookupResult = {
          bundleId: '',
          found: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
        
        // Cache error results too to prevent retrying immediately
        cacheRef.current.set(cacheKey, errorResult);
        return errorResult;
      } finally {
        // Remove from pending requests
        pendingRequestsRef.current.delete(cacheKey);
      }
    })();

    // Store the pending request
    pendingRequestsRef.current.set(cacheKey, requestPromise);
    
    return requestPromise;
  }, []);

  const lookupMultipleBundleIds = async (bundleIds: string[]): Promise<LookupResult[]> => {
    setIsLoading(true);
    try {
      // Process lookups with small delay to avoid rate limiting
      const results: LookupResult[] = [];
      for (const bundleId of bundleIds) {
        const result = await lookupByBundleId(bundleId);
        results.push(result);
        // Small delay between requests
        if (bundleIds.indexOf(bundleId) < bundleIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      return results;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    lookupByBundleId,
    lookupByTrackId,
    lookupMultipleBundleIds,
    isLoading
  };
};